/**
 * Database Laden - Web Versie
 * Dit script regelt het vullen van de database en updates
 */

import pako from 'pako';
import { ProductDatabaseRow } from '../models';
import { databaseClient } from './database.web';
// @ts-ignore
import { productDatabase } from '../../PRODUCTS';
// import { DEMO_PRODUCTS } from './demoProducts';

// Versies bijhouden (anders krijg je gezeur met oude data)
// Versies bijhouden (anders krijg je gezeur met oude data)
const STORAGE_KEY_DB_VERSION = 'nebulatide_db_version';
const STORAGE_KEY_DB_LOADED = 'nebulatide_db_loaded_v5'; // Weer een nieuwe versie
const STORAGE_KEY_LAST_UPDATE_CHECK = 'nebulatide_last_update_check';

// Huidige db versie (verhoog dit als je de structuur sloopt)
const CURRENT_DB_VERSION = 2;

// Elke 24 uur checken op updates
const UPDATE_CHECK_INTERVAL = 24 * 60 * 60 * 1000;

// Gecomprimeerd is sneller (want minder MB's)
const USE_COMPRESSED = true;

export interface LoadProgress {
  percentage: number;
  message: string;
}

interface DatabaseExport {
  version: number;
  exportDate: string;
  productCount: number;
  products: ProductDatabaseRow[];
}

/**
 * Is dit de eerste keer? (database niet geladen)
 */
export function isFirstLaunch(): boolean {
  const loaded = localStorage.getItem(STORAGE_KEY_DB_LOADED);
  return loaded !== 'true';
}

/**
 * Welke versie hebben we nu?
 */
export function getCurrentDatabaseVersion(): number {
  const version = localStorage.getItem(STORAGE_KEY_DB_VERSION);
  return version ? parseInt(version, 10) : 0;
}

/**
 * Versie opslaan in local storage
 */
function setDatabaseVersion(version: number): void {
  localStorage.setItem(STORAGE_KEY_DB_VERSION, version.toString());
}

/**
 * Zeggen dat de database geladen is
 */
function markDatabaseAsLoaded(): void {
  localStorage.setItem(STORAGE_KEY_DB_LOADED, 'true');
}

/**
 * Wanneer hebben we voor het laatst gecheckt?
 */
function getLastUpdateCheck(): number {
  const timestamp = localStorage.getItem(STORAGE_KEY_LAST_UPDATE_CHECK);
  return timestamp ? parseInt(timestamp, 10) : 0;
}

/**
 * Opslaan dat we net gecheckt hebben
 */
function setLastUpdateCheck(): void {
  localStorage.setItem(STORAGE_KEY_LAST_UPDATE_CHECK, Date.now().toString());
}

/**
 * Moeten we checken op updates?
 */
export function shouldCheckForUpdates(): boolean {
  const lastCheck = getLastUpdateCheck();
  const now = Date.now();
  return (now - lastCheck) > UPDATE_CHECK_INTERVAL;
}

/**
 * Load database from JSON file (supports both compressed and uncompressed)
 */
/**
 * JSON dingetjes omzetten naar Database rijen
 */
function convertProductsJsToDbRows(products: any[]): ProductDatabaseRow[] {
  return products.map(p => {
    const reasons = p.reasons || [];
    return {
      Nummer: p.id,
      Product: p.name,
      Merk: p.brand,
      Categorie: p.category,
      'Milieu Rating (A+ tot E-)': p.rating,
      'Waarom deze rating? (Impact op water/aquatisch leven)': reasons.join(' '), // Combine all reasons for summary
      Reason_1: reasons[0] || '',
      Reason_2: reasons[1] || '',
      Reason_3: reasons[2] || '',
      Reason_4: reasons[3] || '',
      Barcode: p.barcode,
      ImageUrl: p.imageUrl,
    } as ProductDatabaseRow;
  });
}

/**
 * Load database from JSON file (supports both compressed and uncompressed)
 */
// Fallback naar PRODUCTS.JS als het echt moet (backup plan)
const FALLBACK_PRODUCTS: ProductDatabaseRow[] = convertProductsJsToDbRows(productDatabase);

/**
 * Database inladen uit een JSON bestand (kan ook compressed zijn)
 */
  async function loadDatabaseFromJson(url: string, compressed: boolean = USE_COMPRESSED): Promise<DatabaseExport> {
  const actualUrl = compressed ? `${url}.gz` : url;
  console.log(`Database laden van ${actualUrl}...`);

  try {
    const response = await fetch(actualUrl);

    if (!response.ok) {
      throw new Error(`Kon database niet ophalen: ${response.status} ${response.statusText}`);
    }

    let data: DatabaseExport;

    if (compressed) {
      // Compressed data inladen
      const compressedData = await response.arrayBuffer();
      console.log(`Gedownload: ${(compressedData.byteLength / 1024).toFixed(2)} KB (compressed)`);

      // Decompressen die handel (met pako)
      const decompressed = pako.ungzip(new Uint8Array(compressedData), { to: 'string' });
      data = JSON.parse(decompressed) as DatabaseExport;
    } else {
      // Gewone data (voor als compressie stuk is)
      data = await response.json() as DatabaseExport;
    }



    // Demo producten samenvoegen
    // We vinden PRODUCTS.JS belangrijker dan de JSON want de gebruiker wil dat
    const productMap = new Map();
    
    // data.products.forEach(p => productMap.set(p.Barcode, p));
    
    // PRODUCTS.JS eroverheen gooien
    FALLBACK_PRODUCTS.forEach(p => productMap.set(p.Barcode, p));

    data.products = Array.from(productMap.values());
    data.productCount = data.products.length;

    console.log(`Database geladen: versie ${data.version}, ${data.productCount} producten (van PRODUCTS.JS)`);

    return data;
  } catch (error) {
    console.error('Fout bij laden database van JSON:', error);
    console.warn('Terugvallen op PRODUCTS.JS data...');

    // Geef maar gewoon de fallback data terug
    return {
      version: 1,
      exportDate: new Date().toISOString(),
      productCount: FALLBACK_PRODUCTS.length,
      products: FALLBACK_PRODUCTS,
    };
  }
}

/**
 * Database vullen met spullen
 */
async function populateDatabase(products: ProductDatabaseRow[]): Promise<void> {
  console.log(`Database vullen met ${products.length} producten...`);

  try {
    // Clear existing products
    await databaseClient.clearProducts();

    // Eerst alles weggooien
    await databaseClient.clearProducts();

    // In stukjes toevoegen anders crasht ie (batching)
    const BATCH_SIZE = 500;
    let inserted = 0;

    for (let i = 0; i < products.length; i += BATCH_SIZE) {
      const batch = products.slice(i, i + BATCH_SIZE);
      await databaseClient.bulkInsertProducts(batch);
      inserted += batch.length;
      console.log(`${inserted}/${products.length} producten toegevoegd...`);
    }

    console.log('Database vullen succesvol afgerond');
  } catch (error) {
    console.error('Fout bij vullen database:', error);
    throw new Error(
      `Kon database niet vullen: ${error instanceof Error ? error.message : 'Onbekende fout'}`
    );
  }
}

/**
 * Database initialiseren bij eerste keer opstarten
 * Laadt spullen uit de meegeleverde JSON
 */
export async function initializeDatabaseOnFirstLaunch(): Promise<void> {
  // Check if database is empty
  let needsInit = isFirstLaunch();
  
  if (!needsInit) {
    // Double check if it's really empty (maybe localStorage lied or specific clear happened)
    const count = await databaseClient.getProductCount();
    if (count === 0) {
      console.log('Database lijkt leeg (count=0), dus we gaan toch initialiseren.');
      needsInit = true;
    }
  }

  if (!needsInit) {
    console.log('Database is er al, we doen niks.');
    return;
  }

  console.log('Database initialisatie nodig, vullen maar...');

  try {
    // Laden van bundled JSON
    const data = await loadDatabaseFromJson('/data/products.json');

    // Vullen die hap
    await populateDatabase(data.products);

    // Update version and mark as loaded
    setDatabaseVersion(data.version);
    markDatabaseAsLoaded();

    console.log('✅ Database succesvol geïnitialiseerd');
  } catch (error) {
    console.error('Kon database niet initialiseren:', error);
    throw error;
  }
}

/**
 * Checken op updates
 * Vergelijkt lokale versie met remote versie
 */
export async function checkForDatabaseUpdates(): Promise<boolean> {
  if (!shouldCheckForUpdates()) {
    console.log('Update check is nog niet nodig');
    return false;
  }

  console.log('Checken of er updates zijn...');
  setLastUpdateCheck();

  try {
    // In het echt zou je dit via een API doen
    // Maar nu checken we gewoon de products.json
    const data = await loadDatabaseFromJson('/data/products.json');
    const currentVersion = getCurrentDatabaseVersion();

    if (data.version > currentVersion) {
      console.log(`Update available: v${currentVersion} -> v${data.version}`);

      // Database bijwerken
      await populateDatabase(data.products);
      setDatabaseVersion(data.version);

      console.log('✅ Database is weer helemaal bij');
      return true;
    } else {
      console.log('Database is nog vers genoeg');
      return false;
    }
  } catch (error) {
    console.error('Update check ging mis:', error);
    // Niet crashen, updates boeien nu even niet
    return false;
  }
}

/**
 * Database laden met voortgangsbalkje
 * Geeft promises terug met status updates
 */
export async function loadDatabaseWithProgress(
  onProgress?: (progress: number, message: string) => void
): Promise<void> {
  try {
    onProgress?.(0, 'Loading database...');

    const data = await loadDatabaseFromJson('/data/products.json');
    onProgress?.(30, 'Database geladen, nu invullen...');

    // Eerst wissen
    await databaseClient.clearProducts();
    onProgress?.(40, 'Oude troep weggegooid...');

    // In stukjes toevoegen
    const BATCH_SIZE = 500;
    const totalProducts = data.products.length;
    let inserted = 0;

    for (let i = 0; i < totalProducts; i += BATCH_SIZE) {
      const batch = data.products.slice(i, i + BATCH_SIZE);
      await databaseClient.bulkInsertProducts(batch);
      inserted += batch.length;

      const progress = 40 + Math.floor((inserted / totalProducts) * 50);
      onProgress?.(progress, `${inserted}/${totalProducts} producten toegevoegd...`);
    }

    // Versie bijwerken
    setDatabaseVersion(data.version);
    markDatabaseAsLoaded();

    onProgress?.(100, 'Klaar is kees!');
  } catch (error) {
    console.error('Error loading database with progress:', error);
    throw error;
  }
}

/**
 * Database resetten (voor als alles stuk is)
 */
export async function resetDatabase(): Promise<void> {
  console.log('Resetting database...');

  try {
    await databaseClient.clearProducts();
    localStorage.removeItem(STORAGE_KEY_DB_VERSION);
    localStorage.removeItem(STORAGE_KEY_DB_LOADED);
    localStorage.removeItem(STORAGE_KEY_LAST_UPDATE_CHECK);

    console.log('✅ Database reset successfully');
  } catch (error) {
    console.error('Error resetting database:', error);
    throw error;
  }
}
export const databaseLoader = {
  needsInitialLoad: isFirstLaunch,
  loadDatabase: loadDatabaseWithProgress,
};
